// @flow
import React, { Component, Fragment, createRef } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router';
import type { History } from './router';
import moment from 'moment';
import environment from './environment';
import routes from './routes';

/* Containers and static Components to bind to routes */
import AppleContainer from './modules/apple/containers/AppleContainer';
// import HomeContainer from './modules/home/containers/HomeContainer';
import AboutUsContainer from './modules/about-us/containers/AboutUsContainer';
import Comparative from './modules/comparative/components/Comparative';
import FooterContainer from './modules/footer/containers/FooterContainer';
import LoginContainer from './modules/auth/containers/LoginContainer';
import SignUpContainer from './modules/auth/containers/SignUpContainer';
import NavbarContainer from './modules/navbar/containers/NavbarContainer';
import PreferencesContainer from './modules/preferences/containers/PreferencesContainer';
import NewFlightContainer from './modules/new-flight/containers/NewFlightContainer';
import EditFlightContainer from './modules/edit-flight/containers/EditFlightContainer';
import NewOrderContainer from './modules/new-order/containers/NewOrderContainer';
import FlightContainer from './modules/flight/containers/FlightContainer';
import FlightsComponent from './modules/flights/components/Flights';
import FlightLandingContainer from './modules/flight-landing/containers/FlightLandingContainer';
import OrdersComponent from './modules/orders/components/Orders';
// import ProfileContainer from './modules/profile/containers/ProfileContainer';
import PaymentComponent from './modules/payments/components/Payments';
import OrderContainer from './modules/order/containers/OrderContainer';
import ReserveContainer from './modules/reserve/containers/ReserveContainer';
import ContactContainer from './modules/contact/containers/ContactContainer';
import NotFound from './modules/error/components/404/404';
import PrivacyPolicyContainer from './modules/privacy-policy/containers/PrivacyPolicyContainer';
import TermsServiceContainer from './modules/terms-service/containers/TermsServiceContainer';
import AuthContainer from './modules/auth/containers/AuthContainer'
import CountrySelectModal from './modules/locale/components/CountrySelectModal/CountrySelectModal';
import BestDealsContainer from './modules/best-deals/containers/BestDealsContainer';
import StoresContainer from './modules/stores/containers/StoresContainer';
import StoreContainer from './modules/store/containers/StoreContainer';
import ProductListContainer from './modules/product-list/containers/ProductListContainer';
import WishListContainer from './modules/wish-list/containers/WishListContainer';
import FeaturedProductsContainer from './modules/featured-products/container/FeatureProductsContainer';
import PopularProductsContainer from './modules/popular-products/container/PopularProductsContainer';
import CustomProducts from './modules/custom-products/components/CustomProducts';
import CargoContainer from './modules/cargo/container/CargoContainer';
import TravelerTestimonials from './modules/testimonials/components/TravelerTestimonials';
import WishListCatalogContainer from './modules/wish-list-catalog/containers/WishListCatalogContainer';
import UnsubscribeSuccessContainer from './modules/unsubscribe/containers/UnsubscribeSuccessContainer';
import MyWishListsContainer from './modules/my-wish-lists/containers/MyWishListsContainer';
import CourierContainer from './modules/courier/containers/CourierContainer';
import LandingTiendasContainer from './modules/tiendas/containers/LandingTiendasContainer';
import HelpTicketContainer from './modules/help-ticket/container/HelpTicketContainer';
// Some startup values
import { actions as LocalValuesActions } from './modules/local-values/duck';
import { actions as LocaleActions } from './modules/locale/duck';
import { actions as AuthActions } from './modules/auth/duck';
import { actions as ReportActions } from './modules/report/duck';
import { actions as PunchCardActions } from './modules/punch-card/duck';
import { actions as ShoppingCartActions } from './modules/shopping-cart/duck';
import { PrivateRoute, AlreadyAuthRoute } from './router';
import { addLocaleData, FormattedMessage, defineMessages, intlShape } from 'react-intl';
// import Checkout from './modules/checkout/components/Checkout';
import { domainRedirectMap } from './server/middleware/redirect';
import analytics from './analytics';
import { getChosenVariant } from './modules/ab-test/utils';
import Page from './Page';
import Modal from './elements/Modal/Modal';
import classNames from 'classnames/bind';
import NewHomeContainer from './modules/home/containers/NewHomeContainer';
import ShoppingCartContainer from './modules/shopping-cart/containers/ShoppingCartContainer';
import ScrapContainer from './modules/scrap/containers/ScrapContainer';

import modalStyle from './assets/styles/modal.scss';
import btnStyle from './assets/styles/button.scss';
import style from './modules/home/components/Home.scss';
import es from 'react-intl/locale-data/es';
import en from 'react-intl/locale-data/en';
import coverImg from './assets/img/meta-cover-lightblue.png';

import type { RootState } from './modules';
import type { LocalValuesState } from './modules/local-values/duck';
import type {User, Country, ShoppingCartItem} from './entities';
import type { Location } from './router';
import type { LocaleState } from './modules/locale/duck';
import type { TrackingData } from './analytics';

type Props = {
  +history: History;
  +locale: LocaleState;
  +localValues: LocalValuesState;
  +authenticated?: boolean;
  +user?: User;
  +fetchCategories: () => void;
  +fetchCountries: () => void;
  +fetchStoreCategories: () => void;
  +fetchOnlineStores: () => void;
  +isAuthing?: boolean;
  +isRegistering?: boolean;
  +location: Location;
  // +trending: TrendingState;
  // +fetchTrending: () => void;
  +resetAuthState: () => void;
  +getSession: () => void;
  +setTrackingSession: (trackingData: TrackingData) => void;
  +motives: any;
  +fetchReportMotives: () => void;
  +fetchPunchCardPoints: () => void;
  +count?: number;
  +total?: number;
  +setDomainCountry: (country: Country) => void;
  +chosenVariants?: { [key: string]: number };
  +shoppingCart?: ShoppingCartItem[];
  +isFetchingCart?: boolean;
  +forecastShoppingCartItem: (itemNumber: number) => void;
  +fetchShoppingCart: () => void;
  +deleteVirtualShoppingCart: () => void;
};

type State = {
  authModalVisible: boolean;
  authModalShowLogin: boolean;
  countrySelectModalVisible: boolean;
};

// Add locales
addLocaleData(es);
addLocaleData(en);
// Set threshold for duration
moment.relativeTimeThreshold('s', 60);
moment.relativeTimeThreshold('m', 60);
moment.relativeTimeThreshold('h', 24);

const messages = defineMessages({
  ogTitle: {
    id: 'qempo.meta.og.title',
  },
  ogDescription: {
    id: 'qempo.meta.og.description',
  },
  twitterTitle: {
    id: 'qempo.meta.twitter.title',
  },
  twitterDescription: {
    id: 'qempo.meta.twitter.description',
  },
});

const googleSiteCode = {
  'US': 'YIMCDbmbRNAFIQ5oWaGOlhxQnSMeU31xosvB_KYSLOw',
  'PE': '_Vzd63mU09ttCcqEm3HA9ir_AjaRHNmor8jmfGVjeNE',
  'CO': 'KuLH8JfK3J-7Q-l1mGZIk5c74O-Xc2L7T-UiFgnPeMI',
};

class App extends Component<Props, State> {
  authContainerRef = createRef();
  state = {
    authModalVisible: false,
    authModalShowLogin: true,
    needVerificationModalVisible: false,
    countrySelectModalVisible: false,
  };
  whatsappLink = "https://wa.me/573176813983?text=Hola%20quiero%20cotizar/comprar%20un%20producto";

  constructor(props: Props) {
    super(props);
    // Set locale globally
    moment.locale(props.locale.locale);
  }

  componentDidMount() {
    const {
      localValues, fetchCategories, fetchCountries, fetchStoreCategories, fetchOnlineStores,
      authenticated, user, getSession, locale, setTrackingSession, motives, fetchReportMotives,
      fetchPunchCardPoints, count, total, setDomainCountry, location,
    } = this.props;
    if (!localValues.categories) {
      fetchCategories();
    }
    if (!localValues.storeCategories) {
      fetchStoreCategories();
    }
    if (!localValues.countries) {
      fetchCountries();
    }
    if (!localValues.onlineStores) {
      fetchOnlineStores();
    }
    if (!motives) {
      fetchReportMotives();
    }
    if (authenticated && (typeof count !== 'number' || typeof total !== 'number')) {
      fetchPunchCardPoints();
    }

    // Try to always get session
    getSession();
    /*if (environment.env === 'local') {
      if (!authenticated && !user) {
        getSession();
      }
    } else {
      // When not in local, and not in SSR, then ask for session
      if (!!window && !window.__SSR__) {
        console.log('No SSR, asking for session');
        getSession();
      }
    }*/

    if (!locale.domainCountry) {
      const s = document.location.host.toLowerCase().split('.')
      const tld = s[s.length - 1];
      let c: Country;

      if (tld === 'com') {
        c = 'US';
      } else if (tld === 'co') {
        c = 'CO';
      } else if (tld === 'pe') {
        c = 'PE';
      }

      if (!!c) {
        setDomainCountry(c);
      }
    }

    // We do it through get session api call
    /*analytics.analyticsReady(() => {
      if (!!user && !!authenticated) {
        // NOTE: If it's pre-rendered, then initialize it on frontend
        analytics.setUserContext(user);
      }
    });*/
    // Define function to call if segment is blocked
    window.fallbackTrackingInitializeFn = (function () {
      console.log('Fallback tracking initialization');
      setTrackingSession(analytics.buildTrackingData(locale));
    }).bind(this);
    // Initialize all analytical libs
    analytics.initializeErrorTracking();
    analytics.initializeEventTracking();

    analytics.gaReady((function () {
      console.log('GA ready');
      window.gaReadyApplied = true;
      console.log('Setting tacking session');
      setTrackingSession(analytics.buildTrackingData(locale));
    }).bind(this));

    setTimeout((function () {
      if (window && !window.gaReadyApplied) {
        console.log('Fallback set tacking session');
        setTrackingSession(analytics.buildTrackingData(locale));
      }
    }).bind(this), 5000);

    // NOTE: Activate all ab test experiments
    // analytics.fireCustomEvent('optimize.activate');

    // See if should show the countrySelectModal
    if (locale.domainCountry === 'US'/* &&
      (((!!locale.country && (!domainRedirectMap[locale.country] || locale.country === 'US'))) || !locale.country)*/) {
      this.setState({
        countrySelectModalVisible: true,
      });
    }
    
    /*if(!trending.products || !trending.stores) {
      fetchTrending();
    }*/

    // Load SmartSupp
    // Delete temporary smartsupp
    /*if (locale.domainCountry === 'CO' && environment.env === 'production') {
      if (window.initSmartSupp) {
        window.initSmartSupp(environment.smartsupp);
        console.log('SmartSupp initialized');
      }
    }*/

    // Load Sirena Gadget
    /*if (
      !!window.loadSirenaGadget &&
      environment.env !== 'local' &&
      locale.domainCountry !== 'CO'
    ) {
      window.loadSirenaGadget(locale.domainCountry || 'PE');
    }*/
  }

  componentDidUpdate(prevProps: Props) {
    const { 
      locale, authenticated, shoppingCart, isFetchingCart, forecastShoppingCartItem, 
      fetchShoppingCart, deleteVirtualShoppingCart, user, 
    } = this.props;
    const { authModalVisible } = this.state;
    const scrollAtCheckout: boolean = !(location.pathname.split('/')[3] === 'order' &&
      (prevProps.location.pathname.split('/')[3] === 'bag' ||
        prevProps.location.pathname.split('/')[3] === 'cart'));
    if (prevProps.location.pathname !== location.pathname) {
      // Page view when route changes
      // console.log('location.pathname', location.pathname, location);
      /*analytics.pageView('', {
        title: document.title,
        url: document.location.href,
        path: document.location.pathname,
        referrer: document.referrer,
        search: document.location.search,
      });*/
      if (scrollAtCheckout) {
        window.scrollTo(0, 0);
      }
    }
    // Set locale globally
    moment.locale(locale.locale);
    if (authenticated && authModalVisible) {
      this.setState({
        authModalVisible: false,
      });
    }
    
    if (!user) {
      // fetchVirtualShoppingCart();
      deleteVirtualShoppingCart();
    } else if (typeof shoppingCart === 'undefined' && !isFetchingCart) {
      fetchShoppingCart();
    }
    
    // When shopping cart changes
    if (shoppingCart !== prevProps.shoppingCart && shoppingCart) {
      shoppingCart.forEach((item) => {
        if (!item.forecastClassic && !item.forecastCourier && !item.isLoadingForecast) {
          forecastShoppingCartItem(item.number);
        }
      });
    }
  }

  handleCloseAuthModal() {
    const { isRegistering, isAuthing, resetAuthState } = this.props;
    if (isRegistering || isAuthing) return;
    this.setState({
      authModalVisible: false,
    });
    // resetAuthState();
  }

  handleToggleAuthModal(e: Event) {
    const { resetAuthState } = this.props;
    e.preventDefault();
    this.setState((state: State) => ({ authModalShowLogin: !state.authModalShowLogin }));
    resetAuthState();
  }

  handleOpenAuthModal(openLoginView: boolean = true) {
    const { resetAuthState } = this.props;
    this.setState({
      authModalVisible: true,
      authModalShowLogin: openLoginView,
    });
    resetAuthState();
  }

  handleCloseCountrySelectModal = () => {
    this.setState({ countrySelectModalVisible: false });
  };

  handleOpenSentForgotPasswordModal() {
    if (this.authContainerRef && this.authContainerRef.current) {
      this.authContainerRef.current.getWrappedInstance().handleOpenSentForgotPasswordModal();
    }
  }

  getNavbar = (pathArray: string[], isABTest?: boolean) => {
    const { location } = this.props;
    console.log('pathArray', pathArray);
    if (
      !((pathArray[1] === 'tiendas') || (pathArray[2] === 'checkout') || (pathArray[2] === 'search') || (pathArray[1] === 'scrap'))
    ) {
      return <NavbarContainer location={location}
                              handleOpenAuthModal={this.handleOpenAuthModal.bind(this)}/>;
    }
  }

  render() {
    const { history, user, authenticated, location, locale, localValues, chosenVariants } = this.props;
    const { authModalVisible, authModalShowLogin, countrySelectModalVisible } = this.state;
    const baseUrl = environment.baseUrl[locale.domainCountry || 'US'];
    const metas = [
      { name: 'robots', content: 'index' },
      { name: 'theme-color', content: '#11aefa' },
      { name: 'description', content: this.context.intl.formatMessage(messages.ogDescription) },
      { property: 'og:url', content: `${baseUrl}${history.location.pathname}` },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: this.context.intl.formatMessage(messages.ogTitle) },
      { property: 'og:description', content: this.context.intl.formatMessage(messages.ogDescription) },
      { property: 'og:image', content: `${baseUrl}${coverImg}` },
      { property: 'fb:app_id', content: environment.facebook },
      { name: 'twitter:card', content: 'summary' },
      { name: 'twitter:site', content: '@qempito' },
      { name: 'twitter:title', content: this.context.intl.formatMessage(messages.twitterTitle) },
      {
        name: 'twitter:twitter:description',
        content: this.context.intl.formatMessage(messages.twitterDescription),
      },
      { name: 'twitter:twitter:image', content: `${baseUrl}${coverImg}` },
    ];
    const links = [];

    if (locale.domainCountry && domainRedirectMap[locale.domainCountry]) {
      links.push({
        rel: 'canonical',
        href: domainRedirectMap[locale.domainCountry],
      })
    }

    // Only add the on production
    if (locale.domainCountry && environment.env === 'production') {
      metas.push({
        name: 'google-site-verification',
        content: googleSiteCode[locale.domainCountry],
      });
    }

    const chosenVariant = getChosenVariant(chosenVariants, 'mostEffectiveCheckoutStep');

    const pathArray = location.pathname.split('/');
    const navbar = this.getNavbar(pathArray, !!chosenVariant.index);

    return (
      <Fragment>
        <Helmet key="helmet"
                meta={metas}
                link={links}>
          <title>{this.context.intl.formatMessage(messages.ogTitle)}</title>
          <style type="text/css">{`
            body {
              ${(pathArray[2] === 'checkout' || pathArray[2] === 'search' || pathArray[1] === 'scrap') ? '' : 'padding-top: 72px;'}
            }
            
            #sirena_button {
              display: none;
            }
          `}</style>
          {/*environment.env === 'production' && (
            <Fragment>
              <script src="//static.ads-twitter.com/oct.js" type="text/javascript"></script>
              <script type="text/javascript">{`twttr.conversion.trackPid('o91z0', { tw_sale_amount: 0, tw_order_quantity: 0 });`}</script>
              <noscript>
                <img height="1" width="1" style="display:none;" alt="" src="https://analytics.twitter.com/i/adsct?txn_id=o91z0&p_id=Twitter&tw_sale_amount=0&tw_order_quantity=0" />
                <img height="1" width="1" style="display:none;" alt="" src="//t.co/i/adsct?txn_id=o91z0&p_id=Twitter&tw_sale_amount=0&tw_order_quantity=0" />
              </noscript>
            </Fragment>
          )*/}
        </Helmet>
        <main>
          {navbar}
          <Switch>
            <Route exact path={routes.HOME.routePath}
                   user={user} authenticated={authenticated}
                   render={props => <Page name={routes.HOME.name}>
                     <section>
                       {/* <HomeContainer {...props}/> */}
                       <NewHomeContainer />
                     </section>
                   </Page>}/>
            <Route exact path={routes.SCRAP.routePath}
                   render={props => (
                     <Page name={routes.SCRAP.name}>
                       <ScrapContainer handleOpenAuthModal={this.handleOpenAuthModal.bind(this)} 
                                       {...props} />
                     </Page>
                   )}/>
            <Route exact path={routes.APPLE.routePath}
                   render={_ => <Page name={routes.APPLE.name}><AppleContainer /></Page>}/>
            <Route exact path={routes.VERIFICATION.routePath}
                   render={_ => <Page name={routes.VERIFICATION.name}><Redirect to={'/'}/></Page>}/>
            <Route exact path={routes.RECOVER_ACCOUNT.routePath}
                   render={_ => <Page name={routes.RECOVER_ACCOUNT.name}><Redirect to={'/'}/></Page>}/>
            {/*<Route exact path="/alter-a" component={HomeAlterA}/>*/}
            {/*<Route exact path="/old" component={HomeOld}/>*/}
            <Route exact path={routes.ABOUT_US.routePath}
                   render={props => <Page name={routes.ABOUT_US.name}><AboutUsContainer {...props}/></Page>}/>
            <Route exact path={routes.QEMPO_TIENDA_MIA.routePath}
                   render={_ => <Page name={routes.QEMPO_TIENDA_MIA.name}><Comparative rivalry="tiendaMia"
                                                                                       locale={locale}/></Page>}/>
            {/*<Route exact path={routes.QEMPO_AEROPOST.routePath}
                   render={_ => <Page name={routes.QEMPO_AEROPOST.name}><Comparative rivalry="aeropost"
                                                                                     locale={locale}/></Page>}/>*/}
            <Route exact path={routes.QEMPO_ESHOPEX.routePath}
                   render={_ => <Page name={routes.QEMPO_ESHOPEX.name}><Comparative rivalry="eShopex"
                                                                                    locale={locale}/></Page>}/>
            <Route exact path={routes.QEMPO_GRABR.routePath}
                   render={_ => <Page name={routes.QEMPO_GRABR.name}><Comparative rivalry="grabr"
                                                                                  locale={locale}/></Page>}/>
            <Route exact path={routes.QEMPO_GUVERY.routePath}
                   render={_ => <Page name={routes.QEMPO_GUVERY.name}><Comparative rivalry="guvery"/></Page>}/>
            <Route exact path={routes.QEMPO_SKYBOX.routePath}
                   render={_ => <Page name={routes.QEMPO_SKYBOX.name}><Comparative rivalry="skyBox"/></Page>}/>
            <Route exact path={routes.QEMPO_COMPRAENUSA.routePath}
                   render={_ => <Page name={routes.QEMPO_COMPRAENUSA.name}>
                     <Comparative rivalry="compraEnUsa"/></Page>}/>
            <Route exact path={routes.QEMPO_COMPARTOMIMALETA.routePath}
                   render={_ => <Page name={routes.QEMPO_COMPARTOMIMALETA.name}>
                     <Comparative rivalry="compartoMiMaleta"/>
                   </Page>}/>
            <Route exact path={routes.UNSUBSCRIBE_MAIL_CAMPAIGN.routePath}
                   render={props => <Page name={routes.UNSUBSCRIBE_MAIL_CAMPAIGN.name}>
                     <UnsubscribeSuccessContainer {...props}
                                                  authModalVisible={authModalVisible}
                                                  handleOpenAuthModal={this.handleOpenAuthModal.bind(this)}/>
                   </Page>}/>
            <Route exact path={routes.STORES.routePath}
                   render={props => <Page name={routes.STORES.name}>
                     <StoresContainer {...props}/>
                   </Page>}/>
            <Route exact path={routes.STORE.routePath}
                   render={props => <Page name={routes.STORE.name}>
                     <StoreContainer {...props}/>
                   </Page>}/>
            <Route exact path={routes.PRODUCTS.routePath}
                   render={props => <Page name={routes.PRODUCTS.name}>
                     <ProductListContainer {...props}/>
                   </Page>}/>
            {/*<Route exact path="/product/:title" component={TrendingProductContainer}/>*/}
            {/*<Route exact path="/profile/:id" component={ProfileContainer}/>*/}
            <AlreadyAuthRoute exact path={routes.LOGIN.routePath} component={LoginContainer}
                              name={routes.LOGIN.name}
                              user={user} authenticated={authenticated}
                              handleOpenSentForgotPasswordModal={this.handleOpenSentForgotPasswordModal.bind(this)}/>
            <AlreadyAuthRoute exact path={routes.SIGN_UP.routePath} component={SignUpContainer}
                              name={routes.SIGN_UP.name}
                              user={user} authenticated={authenticated}/>
            <PrivateRoute path={routes.PREFERENCES.routePath} component={PreferencesContainer}
                          name={routes.PREFERENCES.name}
                          user={user} authenticated={authenticated}/>
            <PrivateRoute path={routes.MY_PAYMENTS.routePath} component={PaymentComponent}
                          user={user} authenticated={authenticated}/>
            <Route exact path={routes.NEW_FLIGHT.routePath}
                   render={props => <Page name={routes.NEW_FLIGHT.name}>
                     <NewFlightContainer {...props}
                                         authModalVisible={authModalVisible}
                                         handleOpenAuthModal={this.handleOpenAuthModal.bind(this)}/>
                   </Page>}/>
            <Route exact path={routes.FLIGHT_LANDING.routePath} component={FlightLandingContainer} />
            <PrivateRoute exact path="/flight/:id/edit" component={EditFlightContainer}
                          user={user} authenticated={authenticated}/>
            <Route path={routes.NEW_ORDER.routePath}
                   render={props => <NewOrderContainer {...props}
                                                       authModalVisible={authModalVisible}
                                                       handleOpenAuthModal={this.handleOpenAuthModal.bind(this)}/>
                   }/>
            <Route exact path={routes.CONTACT.routePath}
                   render={props => <Page name={routes.CONTACT.name}><ContactContainer {...props}/></Page>}/>
            <PrivateRoute path={routes.FLIGHTS.routePath} component={FlightsComponent}
                          user={user} authenticated={authenticated}/>
            <PrivateRoute path={routes.ORDERS.routePath} component={OrdersComponent}
                          user={user} authenticated={authenticated}/>
            <PrivateRoute path={routes.PURCHASE.routePath} component={OrderContainer}
                          user={user} authenticated={authenticated}/>
            <PrivateRoute path={routes.SALE.routePath} component={OrderContainer}
                          user={user} authenticated={authenticated}/>
            <PrivateRoute path={routes.RESERVE.routePath} component={ReserveContainer}
                          name={routes.RESERVE.name}
                          exact user={user} authenticated={authenticated}/>
            <PrivateRoute path={routes.FLIGHT.routePath} component={FlightContainer}
                          user={user} authenticated={authenticated}/>
            <Redirect from="/flight" exact to="/"/>
            <Route exact path={routes.TERMS_OF_SERVICE.routePath}
                   render={props => <Page name={routes.TERMS_OF_SERVICE.name}>
                     <TermsServiceContainer {...props}/>
                   </Page>}/>
            <Route exact path={routes.PRIVACY_POLICY.routePath}
                   render={props => <Page name={routes.PRIVACY_POLICY.name}>
                     <PrivacyPolicyContainer {...props}/>
                   </Page>}/>
            {/*<Route exact path={routes.HELP_TICKET.routePath}
                   render={props => <Page name={routes.HELP_TICKET.name}>
                     <HelpTicketContainer {...props}/>
                   </Page>}/>*/}
            <Route exact path={routes.BEST_DEALS.routePath}
                   render={props => <Page name={routes.BEST_DEALS.name}>
                     <BestDealsContainer {...props}/>
                   </Page>}/>
            <Route exact
                   path={routes.MY_WISH_LISTS.routePath}
                   render={props => <Page
                     name={routes.MY_WISH_LISTS.name}><MyWishListsContainer {...props}/></Page>}/>
            <Route exact
                   path={routes.WISH_LIST_CATALOG.routePath}
                   render={props => <Page
                     name={routes.WISH_LIST_CATALOG.name}><WishListCatalogContainer {...props}/></Page>}/>
            <Route exact path={routes.WISH_LIST_USER.routePath}
                   render={props => <Page name={routes.WISH_LIST_USER.name}>
                     <WishListContainer {...props}
                                        authModalVisible={authModalVisible}
                                        handleOpenAuthModal={this.handleOpenAuthModal.bind(this)}/></Page>}/>
            <Route exact path={routes.WISH_LIST_LOCAL.routePath}
                   render={props =>
                     <Page name={routes.WISH_LIST_LOCAL.name}><WishListContainer {...props}/></Page>}/>
            <Route exact path={routes.FEATURED_PRODUCTS.routePath}
                   render={props =>
                     <Page name={routes.FEATURED_PRODUCTS.name}><FeaturedProductsContainer {...props}/></Page>}/>
            <Route exact path={routes.POPULAR_PRODUCTS.routePath}
                   render={props =>
                     <Page name={routes.POPULAR_PRODUCTS.name}><PopularProductsContainer {...props}/></Page>}/>
            <Route path={routes.CUSTOM_PRODUCT.routePath}
                   render={props => <CustomProducts {...props}/>}/>
            <Route path={routes.CARGO.routePath}
                   render={props => <Page name={routes.CARGO.name}>
                     <CargoContainer {...props}
                                     authModalVisible={authModalVisible}
                                     handleOpenAuthModal={this.handleOpenAuthModal.bind(this)}/>
                   </Page>}/>
            <Route render={props => <Page name={routes.SHOPPING_CART.name}><ShoppingCartContainer /></Page>}/>
            <Route path={routes.TESTIMONIALS.children.TRAVELERS.routePath}
                   render={props =>
                     <Page
                       name={routes.TESTIMONIALS.children.TRAVELERS.name}><TravelerTestimonials {...props}/></Page>}/>
            <PrivateRoute path={routes.COURIER.routePath} component={CourierContainer}
                          user={user} authenticated={authenticated}/>
            {/*<Route path={routes.TIENDAS.routePath}
                   render={props => <Page name={routes.TIENDAS.name}>
                     <LandingTiendasContainer {...props}/>
                   </Page>}/>*/}
            <Route render={props => <Page name={routes.NOT_FOUND.name}><NotFound {...props}/></Page>}/>
          </Switch>
          <FooterContainer location={location}/>
        </main>
        <AuthContainer key="auth"
                       ref={this.authContainerRef}
                       authModalVisible={authModalVisible}
                       authModalShowLogin={authModalShowLogin}
                       handleCloseAuthModal={this.handleCloseAuthModal.bind(this)}
                       handleToggleAuthModal={this.handleToggleAuthModal.bind(this)}/>
        <CountrySelectModal countrySelectModalOpen={countrySelectModalVisible}
                            handleClose={this.handleCloseCountrySelectModal}
                            location={location}
                            locale={locale}
                            localValues={localValues}/>
        <Modal open={locale.domainCountry === 'CO'}
               handleClose={() => {
               }}
               big
               showCloseButton={false}
        >
          <div>
            <FormattedMessage id="home.banner.colombia.close.title">{
              txt => <h1 className={modalStyle.title}>{txt}</h1>
            }</FormattedMessage>
            <FormattedMessage id="home.banner.colombia.close.button">{
              txt => <a
                className={classNames(
                  btnStyle.btn,
                  btnStyle.lightBlue,
                  style.modalButton)
                }
                href={this.whatsappLink}
                target="_blank"
              >{txt}</a>
            }</FormattedMessage>
          </div>
        </Modal>
      </Fragment>);
  }
}

App.propTypes = {
  history: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  authenticated: PropTypes.bool,
  user: PropTypes.object,
  fetchCategories: PropTypes.func.isRequired,
  fetchCountries: PropTypes.func.isRequired,
  fetchStoreCategories: PropTypes.func.isRequired,
  fetchOnlineStores: PropTypes.func.isRequired,
  isAuthing: PropTypes.bool,
  isRegistering: PropTypes.bool,
  location: PropTypes.object.isRequired,
  // trending: PropTypes.object,
  // fetchTrending: PropTypes.func.isRequired,
  resetAuthState: PropTypes.func.isRequired,
  getSession: PropTypes.func.isRequired,
  setTrackingSession: PropTypes.func.isRequired,
  motives: PropTypes.object,
  fetchReportMotives: PropTypes.func,
  fetchPunchCardPoints: PropTypes.func.isRequired,
  count: PropTypes.number,
  total: PropTypes.number,
  setDomainCountry: PropTypes.func.isRequired,
  chosenVariants: PropTypes.object,
  shoppingCart: PropTypes.array,
  isFetchingCart: PropTypes.bool,
  forecastShoppingCartItem: PropTypes.func.isRequired,
  fetchShoppingCart: PropTypes.func.isRequired,
  deleteVirtualShoppingCart: PropTypes.func.isRequired,
};

App.contextTypes = {
  intl: intlShape,
};

function mapStateToProps(state: RootState) {
  return {
    localValues: state.localValues,
    locale: state.locale,
    authenticated: state.auth.authenticated,
    user: state.auth.user,
    isAuthing: state.auth.isAuthing,
    isRegistering: state.auth.isRegistering,
    motives: state.report.motives,
    // trending: state.trending
    count: state.punchCard.count,
    total: state.punchCard.total,
    shoppingCart: state.shoppingCart.shoppingCart,
    isFetchingCart: state.shoppingCart.isFetchingCart,
  }
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({
    ...LocalValuesActions, ...AuthActions,
    ...ReportActions, /*...TrendingsAction*/
    ...PunchCardActions, ...LocaleActions,
    ...ShoppingCartActions,
  }, dispatch);
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));



// WEBPACK FOOTER //
// ./src/App.jsx